import React, { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { ReactComponent as ArrowLeftIcon } from "../../assets/arrow_left.svg";

const StyledArrowIcon = styled(ArrowLeftIcon)`
  width: 18px;
  height: 15px;
  fill: #00aeef;

  path {
    fill: #00aeef;
  }
`;

const BackButtonContainer = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  margin: 0 0 12px 0;
  background: none;
  border: none;
  cursor: pointer;
  font-family: Roboto Condensed, sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #00aeef;
  text-transform: none;

  &:hover {
    opacity: 0.75;
  }
`;

const isOpenedInNewTabOrWindow = (history) => {
  if (typeof window === "undefined") return false;
  if (window.opener) return true;
  if (window.history.length <= 1) return true;

  if (history.action === "PUSH" || history.action === "REPLACE") {
    return false;
  }

  const navType = performance.getEntriesByType?.("navigation")?.[0]?.type;
  return navType === "navigate" && window.history.length <= 2;
};

const BackButton = ({
  label = "Back",
  to,
  fallbackPath,
  onClick,
  className,
  style,
  skipSamePathname = true,
}) => {
  const history = useHistory();
  const unlistenRef = useRef(null);

  useEffect(() => {
    return () => {
      if (unlistenRef.current) {
        unlistenRef.current();
        unlistenRef.current = null;
      }
    };
  }, []);

  if (isOpenedInNewTabOrWindow(history)) {
    return null;
  }

  const goBackToPreviousPage = () => {
    const startPath = history.location.pathname;
    let hops = 0;
    const maxHops = 40;

    if (unlistenRef.current) {
      unlistenRef.current();
      unlistenRef.current = null;
    }

    const unlisten = history.listen((location) => {
      if (location.pathname !== startPath) {
        unlisten();
        unlistenRef.current = null;
        return;
      }

      hops += 1;
      if (hops >= maxHops || window.history.length <= 1) {
        unlisten();
        unlistenRef.current = null;
        if (fallbackPath) {
          history.replace(fallbackPath);
        }
        return;
      }

      history.goBack();
    });
    unlistenRef.current = unlisten;

    if (history.length > 1) {
      history.goBack();
      return;
    }

    unlisten();
    unlistenRef.current = null;
    if (fallbackPath) {
      history.push(fallbackPath);
    }
  };

  const handleClick = (event) => {
    if (onClick) {
      onClick(event);
      return;
    }

    if (to) {
      history.push(to);
      return;
    }

    if (skipSamePathname) {
      goBackToPreviousPage();
      return;
    }

    if (history.length > 1) {
      history.goBack();
      return;
    }

    if (fallbackPath) {
      history.push(fallbackPath);
    }
  };

  return (
    <BackButtonContainer
      type="button"
      onClick={handleClick}
      className={className}
      style={style}
      aria-label={label || "Back"}
    >
      <StyledArrowIcon />
      {label ? <span>{label}</span> : null}
    </BackButtonContainer>
  );
};

export default BackButton;
